import React from "react";
import {
  Ear,
  FolderSearch,
  MapPin,
  Layers,
  CheckCircle,
  Package,
} from "lucide-react";

const STAGES = [
  {
    icon: Ear,
    title: "Listen to Country",
    intro:
      "Initial conversations with Traditional Owners, Elders, rangers and community representatives.",
    blockTitle: "Objectives",
    items: [
      "Understand local priorities",
      "Confirm cultural protocols",
      "Define project outcomes",
    ],
  },
  {
    icon: FolderSearch,
    title: "Gather Existing Knowledge",
    intro: "Review available information including:",
    items: [
      "Healthy Country Plans",
      "Indigenous Protected Area plans",
      "Species records",
      "Ecological assessments",
      "Historical records",
      "Existing mapping products",
    ],
  },
  {
    icon: MapPin,
    title: "On-Country Mapping",
    intro: "Workshops and field visits to identify:",
    items: [
      "Significant species",
      "Landscape features",
      "Cultural values",
      "Restoration priorities",
      "Seasonal knowledge",
      "Areas of opportunity and concern",
    ],
  },
  {
    icon: Layers,
    title: "Build the Bush Map",
    intro:
      "Information is translated into an illustrated map showing the interconnected layers of Country. Typical layers include:",
    layers: [
      {
        name: "Top Layer",
        body: "Birds and canopy species",
      },
      {
        name: "Middle Layers",
        body: "Trees, shrubs, mammals, reptiles, cultural uses",
      },
      {
        name: "Bottom Layers",
        body: "Soils, hydrology, geology, carbon and restoration opportunities",
      },
    ],
  },
  {
    icon: CheckCircle,
    title: "Community Review",
    intro:
      "Draft maps are reviewed with community members to ensure accuracy, cultural appropriateness and local ownership.",
  },
  {
    icon: Package,
    title: "Final Products",
    intro:
      "Depending on project objectives, outputs may include:",
    items: [
      "Large-format Bush Map",
      "Funding and investment brief",
      "Ranger training resources",
      "School education materials",
      "Community displays",
      "Interactive digital products",
    ],
  },
];

export default function BushMapProcess() {
  return (
    <section
      id="process"
      data-testid="section-process"
      className="relative py-24 md:py-32 bg-[#EBE4D8]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <div className="bm-eyebrow mb-6">Section 05 — Process</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
            Typical Bush Mapping Process
          </h2>
          <p className="mt-6 text-[#584D45] text-lg leading-relaxed">
            Each project is shaped around community priorities, but a typical
            Bush Mapping engagement follows these six stages.
          </p>
        </div>

        {/* Timeline */}
        <ol className="relative">
          {/* Vertical line */}
          <span
            className="absolute left-[28px] md:left-[35px] top-3 bottom-3 w-px bg-[#C5B6A3]"
            aria-hidden="true"
          />
          {STAGES.map((s, i) => (
            <li key={s.title} className="relative pl-20 md:pl-28 pb-12 md:pb-14 last:pb-0">
              {/* Marker */}
              <div className="absolute left-0 top-0 flex items-center gap-3">
                <span className="w-14 h-14 md:w-[70px] md:h-[70px] rounded-full bg-[#FDFBF7] border border-[#C5B6A3] flex items-center justify-center text-[#A95A3F] shadow-sm">
                  <s.icon size={22} />
                </span>
              </div>

              <div className="bm-card bg-[#FDFBF7] p-7 md:p-9">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-[#A95A3F] font-semibold">
                    Stage {i + 1}
                  </span>
                  <span className="h-px flex-1 bg-[#E2DACF]" />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#2D2621] leading-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-[#584D45] leading-relaxed">{s.intro}</p>

                {s.blockTitle && (
                  <div className="mt-6 text-xs tracking-[0.24em] uppercase text-[#7A6C63]">
                    {s.blockTitle}
                  </div>
                )}

                {s.items && (
                  <ul className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                    {s.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-3 text-[#2D2621] text-[15px] leading-relaxed"
                      >
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#6B7A6A] shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                )}

                {s.layers && (
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    {s.layers.map((l) => (
                      <div
                        key={l.name}
                        className="p-4 border border-[#E2DACF] bg-[#F3EFEA]"
                      >
                        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#A95A3F]">
                          {l.name}
                        </div>
                        <div className="mt-2 text-[#2D2621] text-[15px] leading-snug">
                          {l.body}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
